import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Renderuje children w już otwartym oknie przeglądarki (przekazanym przez `windowInstance`).
 *
 * Okno MUSI być otwarte synchronicznie w handlerze user-gesture (np. onClick mapy),
 * inaczej kolejne `window.open` po pierwszym zamknięciu popupu są blokowane.
 *
 * Props:
 *  - windowInstance  okno otwarte wcześniej przez window.open(); może być null → brak renderu
 *  - title           tytuł popupa
 *  - onClose         wywoływane gdy popup zamknięty (krzyżyk, Ctrl+W, brak okna)
 */
export default function PopupWindow({ windowInstance, title = 'Okno', onClose, children }) {
  const [container, setContainer] = useState(null);

  useEffect(() => {
    const w = windowInstance;
    if (!w || w.closed) {
      if (onClose) onClose();
      return;
    }

    // Wyczyść dokument popupa (gdy okno jest reused) i zbuduj head/body od nowa.
    try {
      const doc = w.document;
      doc.title = title;
      while (doc.head.firstChild) doc.head.removeChild(doc.head.firstChild);
      while (doc.body.firstChild) doc.body.removeChild(doc.body.firstChild);

      // Skopiuj <link rel="stylesheet"> i <style> z głównego dokumentu (Tailwind).
      document.querySelectorAll('link[rel="stylesheet"], style').forEach((node) => {
        doc.head.appendChild(node.cloneNode(true));
      });

      const baseStyle = doc.createElement('style');
      baseStyle.textContent = `html,body{margin:0;padding:0;background:#f3f4f6;font-family:ui-sans-serif,system-ui,-apple-system,"Segoe UI",Roboto,sans-serif}body{padding:16px}`;
      doc.head.appendChild(baseStyle);

      const div = doc.createElement('div');
      doc.body.appendChild(div);
      setContainer(div);
    } catch (_) {
      if (onClose) onClose();
      return;
    }

    const handleBeforeUnload = () => {
      if (onClose) onClose();
    };
    w.addEventListener('beforeunload', handleBeforeUnload);

    const handleParentUnload = () => {
      try { w.close(); } catch (_) {}
    };
    window.addEventListener('beforeunload', handleParentUnload);

    // Polling: Chrome nie zawsze emituje beforeunload przy zamknięciu krzyżykiem.
    const closedPoll = setInterval(() => {
      if (w.closed) {
        clearInterval(closedPoll);
        if (onClose) onClose();
      }
    }, 500);

    return () => {
      clearInterval(closedPoll);
      try { w.removeEventListener('beforeunload', handleBeforeUnload); } catch (_) {}
      window.removeEventListener('beforeunload', handleParentUnload);
      setContainer(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowInstance]);

  // Aktualizuj tytuł i wymuś focus okna na każdej zmianie treści,
  // żeby kolejne kliknięcia w mapę było widać nawet gdy popup był zasłonięty.
  useEffect(() => {
    const w = windowInstance;
    if (!w || w.closed) return;
    try { w.document.title = title; } catch (_) {}
    try { w.focus(); } catch (_) {}
  }, [title, children, windowInstance]);

  if (!container) return null;
  return createPortal(children, container);
}
